import React, { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import "./RapportiniTable.css";

function RapportiniTable({ rapportini, loading, error }) {
  const [sortConfig, setSortConfig] = useState({ key: "data", direction: "desc" });
  const [searchParams, setSearchParams] = useSearchParams();

  // Inizializza con il mese precedente a quello attuale
  const getInitialMonth = () => {
    const today = new Date();
    const previousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    return previousMonth;
  };

  const [selectedMonth, setSelectedMonth] = useState(getInitialMonth());
  const selectedCommessa = searchParams.get("commessa") || "all";

  const rapportiniList = useMemo(
    () => (Array.isArray(rapportini) ? rapportini : rapportini?.rapportini ?? []),
    [rapportini]
  );

  const commesseOptions = useMemo(() => {
    const commesseSet = new Set();
    rapportiniList.forEach((item) => {
      const commessa = item.workOrder || item.commessa || "Senza commessa";
      commesseSet.add(commessa);
    });
    return ["all", ...Array.from(commesseSet).sort()];
  }, [rapportiniList]);

  // Filtra i rapportini per il mese/anno selezionato e per commessa
  const filteredRapportini = useMemo(() => {
    return rapportiniList.filter((item) => {
      const itemDate = new Date(item.data);
      const matchesMonth =
        itemDate.getFullYear() === selectedMonth.getFullYear() &&
        itemDate.getMonth() === selectedMonth.getMonth();
      const commessa = item.workOrder || item.commessa || "Senza commessa";
      const matchesCommessa = selectedCommessa === "all" || commessa === selectedCommessa;
      return matchesMonth && matchesCommessa;
    });
  }, [rapportiniList, selectedMonth, selectedCommessa]);

  const totalHours = filteredRapportini.reduce((sum, item) => sum + (parseFloat(item.workTime) || 0), 0);

  const handlePreviousMonth = () => {
    setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 1));
  };

  const handleCommessaChange = (event) => {
    const nextValue = event.target.value;
    const nextParams = new URLSearchParams(searchParams);

    if (nextValue === "all") {
      nextParams.delete("commessa");
    } else {
      nextParams.set("commessa", nextValue);
    }

    setSearchParams(nextParams);
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  };

  const getCellValue = (item, key) => {
    if (key === "data") return item.data;
    if (key === "internal") return item.internal;
    if (key === "billable") return item.billable;
    if (key === "workTime") return item.workTime;
    return item[key];
  };

  const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.valueOf())) return value;
    return date.toLocaleDateString("it-IT", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  if (loading) {
    return <div className="table-loading">Caricamento rapportini...</div>;
  }

  if (error) {
    return <div className="table-error">Errore nel caricamento dei dati: {error}</div>;
  }

  if (!rapportiniList || rapportiniList.length === 0) {
    return <div className="table-empty">Nessun rapportino disponibile</div>;
  }

  if (filteredRapportini.length === 0) {
    return (
      <div className="table-container">
        <div className="month-filter">
          <div className="month-controls">
              <button className="month-nav month-nav-icon" onClick={handlePreviousMonth} aria-label="Mese precedente">
                <span aria-hidden="true">‹</span>
            </button>
            <h3 className="month-display">{formatMonthYear(selectedMonth)}</h3>
              <button className="month-nav month-nav-icon" onClick={handleNextMonth} aria-label="Mese successivo">
                <span aria-hidden="true">›</span>
            </button>
          </div>

          <div className="right-controls">
            <div className="filter-commessa">
              <select
                id="commessa-filter"
                value={selectedCommessa}
                onChange={handleCommessaChange}
              >
                {commesseOptions.map((commessa) => (
                  <option key={commessa} value={commessa}>
                    {commessa === "all" ? "Tutte le commesse" : commessa}
                  </option>
                ))}
              </select>
            </div>
            <div className="total-hours">
              Ore totali: <strong>{totalHours}</strong>
            </div>
          </div>
        </div>
        <div className="table-empty">Nessun rapportino disponibile per il mese selezionato</div>
      </div>
    );
  }

  const sortedData = [...filteredRapportini].sort((a, b) => {
    const aValue = getCellValue(a, sortConfig.key);
    const bValue = getCellValue(b, sortConfig.key);

    if (aValue === bValue) return 0;
    if (aValue == null) return sortConfig.direction === "asc" ? -1 : 1;
    if (bValue == null) return sortConfig.direction === "asc" ? 1 : -1;

    if (typeof aValue === "string" && typeof bValue === "string") {
      return sortConfig.direction === "asc"
        ? aValue.localeCompare(bValue, "it", { sensitivity: "base" })
        : bValue.localeCompare(aValue, "it", { sensitivity: "base" });
    }

    return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
  });

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return " ↕";
    return sortConfig.direction === "asc" ? " ↑" : " ↓";
  };

  return (
    <div className="table-container">
      <div className="month-filter">
        <div className="month-controls">
          <button className="month-nav month-nav-icon" onClick={handlePreviousMonth} aria-label="Mese precedente">
            <span aria-hidden="true">‹</span>
          </button>
          <h3 className="month-display">{formatMonthYear(selectedMonth)}</h3>
          <button className="month-nav month-nav-icon" onClick={handleNextMonth} aria-label="Mese successivo">
            <span aria-hidden="true">›</span>
          </button>
        </div>

        <div className="right-controls">
          <div className="filter-commessa">
            <select
              id="commessa-filter"
              value={selectedCommessa}
              onChange={handleCommessaChange}
            >
              {commesseOptions.map((commessa) => (
                <option key={commessa} value={commessa}>
                  {commessa === "all" ? "Tutte le commesse" : commessa}
                </option>
              ))}
            </select>
          </div>
          <div className="total-hours">
            Ore totali: <strong>{totalHours}</strong>
          </div>
        </div>
      </div>

      <table className="rapportini-table">
        <thead>
          <tr>
            <th onClick={() => handleSort("data")} className="sortable">
              Data{getSortIndicator("data")}
            </th>
            <th onClick={() => handleSort("workOrder")} className="sortable">
              Commessa{getSortIndicator("workOrder")}
            </th>
            <th onClick={() => handleSort("workTime")} className="sortable">
              Ore Lavoro{getSortIndicator("workTime")}
            </th>
            <th onClick={() => handleSort("onSite")} className="sortable">
              Presenza Sito{getSortIndicator("onSite")}
            </th>
            <th onClick={() => handleSort("description")} className="sortable">
              Descrizione{getSortIndicator("description")}
            </th>
            <th onClick={() => handleSort("operatore")} className="sortable">
              Operatore{getSortIndicator("operatore")}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((item, index) => (
            <tr key={index} className={`row-${item.locked ? "locked" : "unlocked"}`}>
              <td>{formatDate(item.data)}</td>
              <td>{item.workOrder}</td>
              <td className="ore">{item.workTime}</td>
              <td>{item.onSite ? "Sì" : "No"}</td>
              <td>{item.description}</td>
              <td>{item.operatore}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default RapportiniTable;
